import {useCallback, useEffect, useState, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {
  getErrorMessage,
  MultiPayment,
  PAYMENT_PROVIDERS,
  PaymentsCart,
} from '@chekin/core';
import {useEvent} from '@chekinapp/ui';
import {
  useBuildTotalPayments,
  usePayment,
  usePaymentProcessingState,
  usePaymentSettings,
} from '../../hooks';
import {useAddonPayments} from 'hooks/payments/useAddonPayments';
import {useFetchAddonMultiPayment} from 'components/AddonPaymentForm/useFetchAddonMultiPayment';
import {StatusModalStates} from '@chekin/shared';
import {
  PaymentAmounts,
  PaymentCardFace,
  PaymentFlowShell,
  PaymentProgressActions,
  PaymentProgressBody,
  buildPaymentTimeline,
  getCardFaceState,
  getFailureReason,
  getHoldSummary,
  usePaymentFlowPhase,
} from '@chekin/shared/payments';
import {formatMoney} from 'utils/money';
import {ProviderForm} from './ProviderForm';

type Props = {
  isLoading: boolean;
  onSuccess: () => void;
  multiPayment?: MultiPayment;
  paymentsCart?: PaymentsCart;
  successActions?: ReactNode;
  onReceiptsReady?: () => void;
  onUnavailable: () => void;
};

function AddonPaymentForm({
  paymentsCart,
  multiPayment,
  isLoading,
  onSuccess,
  successActions,
  onReceiptsReady,
  onUnavailable,
}: Props) {
  const {t} = useTranslation();
  const [isPaymentFormLoading, setIsPaymentFormLoading] = useState(true);
  const {
    processState,
    progress,
    failureCode,
    onProgress,
    onPaymentSuccess,
    onPaymentError,
    setProcessState,
    closeProcessingModal,
  } = usePaymentProcessingState({onSuccess, autoDismissSuccess: false});

  const {phase, isPaying} = usePaymentFlowPhase({
    processState,
    hasSuccessActions: Boolean(successActions),
    onReceiptsReady,
  });

  const {paymentProvider, currencySymbol} = usePaymentSettings();
  const {totalToPayNow} = useBuildTotalPayments({paymentsCart});
  const chargeTotal = Number(totalToPayNow) || 0;
  const {heldTotal, isAuthorisation} = getHoldSummary(paymentsCart);

  const startProcessing = useCallback(() => {
    setProcessState(StatusModalStates.LOADING);
  }, [setProcessState]);

  const isAddonProvider = paymentProvider === PAYMENT_PROVIDERS.addon;

  const {data: hppData, isLoading: isHppDataLoading} = useFetchAddonMultiPayment({
    id: multiPayment?.id,
    enabled: !isPaying,
  });

  const {resumeListening} = usePayment({
    onPaymentSuccess,
    onPaymentError,
    onProgress,
  });

  const handleAddonPaymentSubmitted = useCallback(() => {
    if (!multiPayment?.id) return;
    startProcessing();
    resumeListening(multiPayment.id);
  }, [multiPayment?.id, resumeListening, startProcessing]);

  useAddonPayments({
    hppData,
    onInit: startProcessing,
    onError: error => {
      console.error('Global Payments initialization error:', error);
      const errorMsg = getErrorMessage(error ?? 'Something went wrong');
      onPaymentError(errorMsg, errorMsg);
    },
    onLoad: () => setIsPaymentFormLoading(false),
    onSuccess: handleAddonPaymentSubmitted,
    enabled: isAddonProvider,
  });

  const handleUnavailable = useEvent(onUnavailable);
  useEffect(() => {
    if (isAddonProvider && !isHppDataLoading && !hppData && multiPayment?.id) {
      handleUnavailable();
    }
  }, [isAddonProvider, isHppDataLoading, hppData, handleUnavailable, multiPayment?.id]);

  const rows = buildPaymentTimeline({
    paymentsCart,
    totalToPayNow: chargeTotal,
    multiPayment: progress,
    hasBankStep: false,
    isBankConfirming: false,
    isFailed: phase === 'error',
  });

  return (
    <PaymentFlowShell
      title={t(isPaying ? 'pay_flow.your_payment' : 'pay_flow.add_card')}
      isPaying={isPaying}
      card={<PaymentCardFace state={getCardFaceState(phase)} isCompact={isPaying} />}
      amounts={
        <PaymentAmounts
          chargeTotal={isLoading ? 0 : chargeTotal}
          holdTotal={heldTotal}
          isAuthorisation={isAuthorisation}
          currencySymbol={currencySymbol}
        />
      }
      footer={
        phase && (
          <PaymentProgressActions
            phase={phase}
            onTryAgain={closeProcessingModal}
            onUseAnotherCard={closeProcessingModal}
            successActions={successActions}
          />
        )
      }
    >
      {phase ? (
        <PaymentProgressBody
          phase={phase}
          rows={rows}
          isBankConfirming={false}
          reason={phase === 'error' ? getFailureReason(progress, failureCode) : undefined}
          amountLabel={formatMoney(chargeTotal, currencySymbol)}
          currencySymbol={currencySymbol}
        />
      ) : (
        <ProviderForm isLoading={isPaymentFormLoading} />
      )}
    </PaymentFlowShell>
  );
}

export {AddonPaymentForm};
